<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('admin-users')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Bulk Upload Users</h1>
        </header>

        <section class="module__actions">
            <button
                v-if="validated"
                class="btn btn-primary"
                :class="{ active: submitted }"
                @click="importUsers()"
            >
                <div>Import {{ users.length }} Users</div>
            </button>

            <a
                v-else
                class="btn"
                :href="downloadUrl(templateFilename)"
                download
            >
                <span class="icon-export"></span> Download Import Template
            </a>
        </section>

        <section class="module__body">
            <form
                v-if="!validated && formLoaded"
                @submit.prevent="upload"
            >
                <div class="form-group">
                    <file-upload
                        :url="`/${organizationSlug}/users`"
                        accept=".csv"
                        @error="(formErrorResponse) => (errorResponse = formErrorResponse)"
                        @success="tempUploadComplete"
                    >
                    </file-upload>
                </div>

                <form-errors :response="errorResponse"></form-errors>

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        :class="{ active: submitted }"
                        :disabled="!tempFileUploaded"
                    >
                        <div>Upload</div>
                    </button>
                </div>
            </form>

            <p
                v-if="rowErrorLength"
                class="bulk-import-error-notification"
            >
                {{ rowErrorLength }} of {{ users.length }} rows had errors. Please review them below and re-upload your
                CSV file.
            </p>

            <table
                v-if="users.length"
                class="index index-view index-import"
            >
                <thead>
                    <tr>
                        <th scope="col">Row</th>
                        <th scope="col">Name</th>
                        <th scope="col">Title</th>
                        <th scope="col">Username</th>
                        <th scope="col">Email</th>
                        <th scope="col">App Repository User</th>
                        <th scope="col">Mission Control User</th>
                        <th scope="col">Relay Manager User</th>
                        <th
                            v-if="organizationHasLicenses"
                            scope="col"
                        >
                            Premium User
                        </th>
                        <th scope="col">Groups</th>
                    </tr>
                </thead>
                <tbody>
                    <!-- eslint-disable vue/no-v-for-template-key -->
                    <template
                        v-for="(user, index) in users"
                        :key="`d${index}`"
                    >
                        <!-- eslint-enable -->
                        <tr :class="{ odd: index % 2 !== 0, 'row-error': index in rowErrors }">
                            <td>{{ index + 1 }}</td>
                            <td>
                                {{ user['name']
                                }}<span
                                    v-if="rowHasError('name', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td>
                                {{ user['title']
                                }}<span
                                    v-if="rowHasError('title', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td>
                                {{ user['username']
                                }}<span
                                    v-if="rowHasError('username', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td>
                                {{ user['email']
                                }}<span
                                    v-if="rowHasError('email', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td>
                                {{ user['app_repository_user']
                                }}<span
                                    v-if="rowHasError('app_repository_user', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td>
                                {{ user['mission_control_user']
                                }}<span
                                    v-if="rowHasError('mission_control_user', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td v-if="organizationHasLicenses">
                                {{ user['premium_user']
                                }}<span
                                    v-if="rowHasError('premium_user', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td>
                                {{ user['relay_manager_user']
                                }}<span
                                    v-if="rowHasError('relay_manager_user', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td v-if="organizationHasLicenses">
                                {{ user['premium_user']
                                }}<span
                                    v-if="rowHasError('premium_user', index)"
                                    class="has-error"
                                    >*</span
                                >
                            </td>
                            <td>
                                <ul>
                                    <li
                                        v-for="(group, groupIndex) in user['groups']"
                                        :key="groupIndex"
                                    >
                                        {{ group
                                        }}<span
                                            v-if="rowHasError(`groups-${index}`, index)"
                                            class="has-error"
                                            >*</span
                                        >
                                    </li>
                                </ul>
                            </td>
                        </tr>

                        <tr
                            v-if="rowErrorLength && rowErrors[index]"
                            :key="`e${index}`"
                            :class="{ odd: index % 2 !== 0 }"
                        >
                            <td colspan="8">
                                <ul class="bulk-import-errors">
                                    <li
                                        v-for="(error, errorIndex) in formattedRowErrors[index]"
                                        :key="errorIndex"
                                    >
                                        {{ error }}
                                    </li>
                                </ul>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </section>
    </div>
</template>

<script>
import * as _ from 'lodash'
import FileUpload from '@/components/FileUpload/index.vue'
import FormErrors from '@/components/FormErrors.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import hasDownloadableFile from '@/mixins/hasDownloadableFile'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import confirmLicenseOverage from './ConfirmLicenseOverage.js'
import fetchLicenseStats from '@/store/modules/licenses/FetchLicenseStats'

export default {
    components: {
        FileUpload,
        FormErrors,
    },

    mixins: [canToast, hasBackButton, hasDownloadableFile, needsOrganizationSlug],

    data() {
        return {
            filenames: {},
            validated: false,
            users: [],
            errorResponse: null,
            rowErrors: {},
            submitted: false,
            tempFileUploaded: false,
            formLoaded: false,
            licensesPurchased: 0,
            licensesUsed: 0,
            licensesHardCap: 0,
        }
    },

    computed: {
        formattedRowErrors() {
            const formatted = {}

            _.forEach(this.rowErrors, (rowError, index) => {
                let errorList = []

                _.forEach(rowError, (errors, property) => {
                    errorList = errorList.concat(errors)
                })

                formatted[index] = errorList
            })

            return formatted
        },

        rowErrorLength() {
            return Object.keys(this.rowErrors).length
        },

        organizationHasLicenses() {
            return this.licensesPurchased !== null && this.licensesPurchased > 0
        },

        templateFilename() {
            if (this.organizationHasLicenses) {
                return 'portal-bulk-prem-user-upload-template.csv'
            }

            return 'portal-bulk-user-upload-template.csv'
        },
    },

    mounted() {
        fetchLicenseStats(this.organizationSlug, (purchasedCount, usedCount, hardCap) => {
            this.licensesPurchased = purchasedCount
            this.licensesUsed = usedCount
            this.licensesHardCap = hardCap
            this.formLoaded = true
        })
    },

    methods: {
        tempUploadComplete(filenames) {
            this.filenames = filenames
            this.errorResponse = null
            this.tempFileUploaded = true
        },

        upload() {
            if (!this.tempFileUploaded || this.submitted) {
                return
            }
            this.submitted = true
            this.$http
                .post(`/${this.organizationSlug}/users/validate-upload`, {
                    temp_filename: this.filenames.temp,
                    original_filename: this.filenames.original,
                })
                .then(({ data }) => {
                    this.validated = true
                    this.submitted = false
                    this.users = data
                    this.errorResponse = null
                    this.title = 'Confirm User Import'
                    this.toast('Users ready to import!')
                })
                .catch((error) => {
                    const { type } = error.response.data

                    if (type === 'row-error') {
                        // handle errors in row data
                        const response = error.response.data
                        this.errorResponse = null
                        this.rowErrors = response.errors
                        this.users = response.users
                    } else {
                        // handle generic error
                        this.errorResponse = error.response
                        this.rowErrors = {}
                        this.users = []
                    }
                    this.submitted = false
                    this.filenames = {}
                    this.tempFileUploaded = false
                })
        },

        importUsers() {
            if (this.submitted) {
                return
            }

            if (this.isExceedingSoftCap()) {
                confirmLicenseOverage(this.licensesPurchased, this.licensesHardCap)
                    .then(() => this.saveUserImport())
                    .catch(() => {})
            } else {
                this.saveUserImport()
            }
        },

        saveUserImport() {
            this.submitted = true
            this.$http
                .post(`/${this.organizationSlug}/users/import-upload`, {
                    temp_filename: this.filenames.temp,
                    user_count: this.users.length,
                })
                .then(({ data }) => {
                    this.toast('Users successfully imported!')
                    this.$router.push(`/${this.organizationSlug}/users`)
                })
                .catch((error) => {
                    this.submitted = false
                    this.errorResponse = error.response
                })
        },

        rowHasError(type, index) {
            return index in this.rowErrors && type in this.rowErrors[index]
        },

        isExceedingSoftCap() {
            const numPremiumUsers = this.users.filter((user) => {
                return user.premium_user && user.premium_user.toLowerCase().includes('on')
            }).length
            const isUploadingPremiumUsers = numPremiumUsers > 0

            return (
                isUploadingPremiumUsers &&
                this.licensesUsed + numPremiumUsers > this.licensesPurchased &&
                this.licensesUsed + numPremiumUsers <= this.licensesHardCap
            )
        },
    },
}
</script>

<style scoped>
.bulk-import-error-notification {
    margin: 45px 0 30px 0;
}

.bulk-import-errors {
    font-family: 'Avenir Heavy';
    font-size: 0.9125em;
}

.bulk-import-errors li {
    margin-bottom: 12px;
}

.bulk-import-errors,
.has-error {
    color: #f96a62;
}

table.index tbody tr:nth-child(even) {
    background-color: #434a4d;
}

.index-import tbody tr {
    background-color: #434a4d;
}

.index-import tbody tr.row-error {
    border-bottom: 0;
}

.index-import tbody tr.odd {
    background-color: #484f52;
}
</style>
